import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import './ClubDetail.css';

const ClubDetail = ({ teamResults }) => {
  const { clubName } = useParams();
  const navigate = useNavigate();
  const decodedClubName = decodeURIComponent(clubName);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [clubName]);

  // Filter teams for this club - add null checks
  const clubTeams = [...new Set(
    teamResults
      .filter(row => row && row['Team'] && row['Team'].startsWith(decodedClubName))
      .map(row => row['Team'])
  )];

  if (clubTeams.length === 0) {
    return (
      <div className="club-detail">
        <div className="club-header">
          <button className="back-button" onClick={() => navigate(-1)}>
            <FiArrowLeft /> Back
          </button>
          <h1>{decodedClubName}</h1>
        </div>
        <div className="no-data">No teams found for this club</div>
      </div>
    );
  }

  // Get all data for this club
  const clubData = teamResults.filter(row => 
    row && row['Team'] && row['Team'].startsWith(decodedClubName)
  );

  // Calculate club-wide stats
  const totalMatches = clubData.reduce((sum, row) => sum + parseInt(row['Matches']) || 0, 0);
  const totalWins = clubData.reduce((sum, row) => sum + parseInt(row['Won']) || 0, 0);
  const totalLosses = clubData.reduce((sum, row) => sum + parseInt(row['Lost']) || 0, 0);
  const overallWinPercent = totalMatches > 0 ? ((totalWins / totalMatches) * 100).toFixed(1) : '0';

  // Group by team and format
  const teamStatsByFormat = {};
  clubData.forEach(row => {
    const team = row['Team'];
    const format = row['Format'];
    const key = `${team}|${format}`;
    
    if (!teamStatsByFormat[key]) {
      teamStatsByFormat[key] = {
        team,
        format,
        data: []
      };
    }
    teamStatsByFormat[key].data.push(row);
  });

  // Get unique formats and sort them
  const formats = [...new Set(clubData.map(row => row['Format']))].sort();

  // Group teams by format
  const teamsByFormat = {};
  formats.forEach(format => {
    teamsByFormat[format] = {};
    Object.values(teamStatsByFormat).forEach(item => {
      if (item.format === format) {
        if (!teamsByFormat[format][item.team]) {
          teamsByFormat[format][item.team] = [];
        }
        teamsByFormat[format][item.team].push(...item.data);
      }
    });
  });

  return (
    <div className="club-detail">
      <div className="club-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <FiArrowLeft /> Back
        </button>
        <h1>{decodedClubName}</h1>
      </div>

      <div className="club-stats-container">
        <h2>Club-Wide Performance</h2>
        <div className="club-stats-grid">
          <div className="stat-card">
            <div className="stat-label">Total Matches</div>
            <div className="stat-value">{totalMatches}</div>
          </div>
          <div className="stat-card highlight">
            <div className="stat-label">Total Wins</div>
            <div className="stat-value">{totalWins}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Total Losses</div>
            <div className="stat-value">{totalLosses}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Win %</div>
            <div className="stat-value">{overallWinPercent}%</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Teams</div>
            <div className="stat-value">{clubTeams.length}</div>
          </div>
        </div>
      </div>

      <div className="teams-breakdown">
        <h2>Performance by Team</h2>
        {formats.map(format => (
          <div key={format} className="format-section">
            <h3 className="format-title">{format}</h3>
            {Object.entries(teamsByFormat[format]).map(([team, data]) => {
              const teamWins = data.reduce((sum, row) => sum + parseInt(row['Won']) || 0, 0);
              const teamMatches = data.reduce((sum, row) => sum + parseInt(row['Matches']) || 0, 0);
              const teamWinPercent = teamMatches > 0 ? ((teamWins / teamMatches) * 100).toFixed(1) : '0';

              return (
                <div key={team} className="team-card">
                  <h3 
                    className="team-name clickable"
                    onClick={() => navigate(`/team/${encodeURIComponent(team)}`)}
                  >
                    {team}
                  </h3>
                  <div className="team-summary">
                    <div className="summary-stat">
                      <span className="label">Matches</span>
                      <span className="value">{teamMatches}</span>
                    </div>
                    <div className="summary-stat">
                      <span className="label">Wins</span>
                      <span className="value highlight">{teamWins}</span>
                    </div>
                    <div className="summary-stat">
                      <span className="label">Win %</span>
                      <span className="value">{teamWinPercent}%</span>
                    </div>
                  </div>

                  <table className="team-matches-table">
                    <thead>
                      <tr>
                        <th>Year</th>
                        <th>Matches</th>
                        <th>Won</th>
                        <th>Lost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data
                        .sort((a, b) => parseInt(b['Year']) - parseInt(a['Year']))
                        .map((row, idx) => (
                          <tr key={idx} className={idx % 2 === 0 ? 'even' : 'odd'}>
                            <td>{row['Year']}</td>
                            <td>{row['Matches']}</td>
                            <td className="highlight">{row['Won']}</td>
                            <td>{row['Lost']}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ClubDetail;
